import React from 'react';
import { DateTime } from 'luxon';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { chatSnapshot, reportTrigger } from '../../../../../particles/api/extension';
import type {
  DiagnosticsReport,
  DiagnosticsMessage,
  DiagnosticsSnapshot
} from '../../../../../particles/api/extension';
import type { Finding } from './findings';

/**
 * Cloud Logging for the project the server runs in. The server writes one JSON
 * line per request with `requestId` (server `routes/middleware/requestId.ts`),
 * so a request id from the extension's trail opens straight onto it.
 */
const GCP_PROJECT = 'salesrook';
export const logsUrl = (requestId: string) =>
  `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(
    `jsonPayload.requestId="${requestId}"`
  )};duration=P7D?project=${GCP_PROJECT}`;

const fmt = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) : '—';

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };

export const Section: React.FC<{ title: string; children: React.ReactNode; action?: React.ReactNode }> = ({
  title,
  children,
  action
}) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ minWidth: 160 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

export const Overview: React.FC<{ report: DiagnosticsReport }> = ({ report }) => {
  const wa = report.environment?.waWebVersion;
  return (
    <Section title={report.referenceCode ?? 'Report'}>
      <Stack direction="row" flexWrap="wrap" gap={3}>
        <Fact label="Sent by">
          {report.user?.name || report.user?.email || report.userId}
          {report.user?.email && report.user?.name ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {report.user.email}
            </Typography>
          ) : null}
        </Fact>
        <Fact label="Agency">{report.user?.companyName ?? '—'}</Fact>
        <Fact label="Sent at">{fmt(report.timestamp)}</Fact>
        <Fact label="Session started">{fmt(report.sessionStart)}</Fact>
        <Fact label="Extension">{report.extensionVersion || '—'}</Fact>
        <Fact label="WhatsApp Web">{wa?.version ? `${wa.version}${wa.build ? ` (${wa.build})` : ''}` : '—'}</Fact>
        <Fact label="Trigger">
          <Chip
            size="small"
            label={reportTrigger({ kind: report.kind, trigger: report.diagnostics?.trigger })}
            color={report.kind === 'manual' ? 'primary' : 'default'}
          />
        </Fact>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, wordBreak: 'break-all' }}>
        {report.environment?.userAgent}
      </Typography>
    </Section>
  );
};

export const Findings: React.FC<{ findings: Finding[] }> = ({ findings }) => (
  <Section title="What this report shows">
    {findings.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        Nothing stands out: the parsers agree, syncs succeeded and no errors were recorded. Check the
        trail below against what the customer described.
      </Typography>
    ) : (
      <Stack gap={1}>
        {findings.map((f, i) => (
          <Alert key={i} severity={f.level} variant="outlined">
            {f.text}
          </Alert>
        ))}
      </Stack>
    )}
  </Section>
);

const download = (filename: string, body: string, type: string) => {
  const url = URL.createObjectURL(new Blob([body], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/** Rows wrapped in the containers the extension's selectors look for, so the
 *  file can be loaded into a DOM and handed straight to the parser. */
const rowsAsHtml = (snap: DiagnosticsSnapshot) =>
  `<!doctype html>\n<html><body><div id="main"><div data-testid="conversation-panel-messages">\n${snap.rowsHtml.join(
    '\n'
  )}\n</div></div></body></html>\n`;

const PAGE_STATE: Record<string, string> = {
  qr: 'QR code (not linked)',
  loading: 'Loading',
  'chat-list': 'Chat list, no chat open',
  'chat-open': 'Chat open',
  unknown: 'Unrecognised'
};

/** The WhatsApp tab as a whole — there even when no chat was open, which is
 *  how most stuck users send a report. */
export const PageState: React.FC<{ report: DiagnosticsReport }> = ({ report }) => {
  const d = report.diagnostics;
  if (!d || !('page' in d)) return null;
  const page = d.page;
  return (
    <Section title="WhatsApp page">
      {!page ? (
        <Typography variant="body2" color="text.secondary">
          {d.whatsAppTabs === 0
            ? 'No WhatsApp Web tab was open.'
            : 'A WhatsApp tab was open but did not answer — the extension had not loaded in it (reload the tab).'}
        </Typography>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
            <Fact label="State">{PAGE_STATE[page.state] ?? page.state}</Fact>
            <Fact label="Tab">
              {page.visible ? 'Visible' : 'Hidden'}
              {page.focused ? ' · focused' : ''}
            </Fact>
            <Fact label="Open for">{Math.round(page.uptimeSec / 60)} min</Fact>
            <Fact label="Chat list rows">{page.counts.chatListRows}</Fact>
            <Fact label="Message rows">{page.counts.messageRows}</Fact>
            <Fact label="Interceptor">
              {page.interceptor.ready ? 'Ready' : 'Not ready'} · {page.interceptor.bufferedMessages} buffered
            </Fact>
            {d.whatsAppTabs != null && d.whatsAppTabs > 1 && <Fact label="WhatsApp tabs">{d.whatsAppTabs}</Fact>}
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
            {page.selectors.map((s) => (
              <Chip
                key={s.name}
                size="small"
                label={s.name}
                color={!s.applicable ? 'default' : s.ok ? 'success' : 'error'}
                variant={s.applicable ? 'filled' : 'outlined'}
                title={!s.applicable ? 'Not applicable on this screen' : s.ok ? 'Found' : 'Not found'}
              />
            ))}
          </Stack>
          <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">
                Page outline ({page.outline.length} elements{page.outlineTruncated ? ', truncated' : ''})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="pre" sx={{ ...MONO, m: 0, maxHeight: 480, overflow: 'auto' }}>
                {page.outline.join('\n')}
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Section>
  );
};

export const ChatSnapshot: React.FC<{ report: DiagnosticsReport }> = ({ report }) => {
  const snap = chatSnapshot(report);
  if (!snap) return null;
  const code = report.referenceCode ?? report.id;
  const dom = snap.dom;
  const icpt = snap.interceptor;
  const icptByHash = new Map((icpt?.messages ?? []).map((m) => [m.textHash, m]));

  const directionCell = (m: DiagnosticsMessage) => {
    const other = icptByHash.get(m.textHash);
    const disagree = other && other.sent !== m.sent;
    return (
      <Stack direction="row" gap={0.5} alignItems="center">
        <Chip size="small" variant="outlined" label={m.sent ? 'Outgoing' : 'Incoming'} />
        {disagree && <Chip size="small" color="error" label={`interceptor: ${other.sent ? 'out' : 'in'}`} />}
      </Stack>
    );
  };

  return (
    <Section
      title="Open chat"
      action={
        <Stack direction="row" gap={1}>
          <Button size="small" onClick={() => download(`${code}-rows.html`, rowsAsHtml(snap), 'text/html')}>
            Download rows (HTML)
          </Button>
          <Button
            size="small"
            onClick={() => download(`${code}.json`, JSON.stringify(report, null, 2), 'application/json')}
          >
            Download report (JSON)
          </Button>
        </Stack>
      }
    >
      <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
        <Fact label="Chat type">{snap.isGroup ? 'Group' : 'One-to-one'}</Fact>
        <Fact label="Rows on page">
          {snap.rowCount} ({snap.virtualizedCount} not rendered)
        </Fact>
        <Fact label="DOM parser">
          {dom ? `${dom.messages.length} messages · ${dom.stats.inCount} in / ${dom.stats.outCount} out` : 'failed'}
        </Fact>
        <Fact label="Interceptor">
          {icpt
            ? `${icpt.messages.length} messages${icpt.ready ? '' : ' (not ready)'}`
            : 'no batch'}
        </Fact>
        <Fact label="Captured">{fmt(snap.capturedAt)}</Fact>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        No message text or names leave the customer&apos;s browser: text is shown as its length, names
        and phone numbers as hashes that only match within this report. Equal text hashes mean the two
        parsers read the same message.
      </Typography>

      {dom && dom.messages.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Time (as shown)</TableCell>
              <TableCell>Length</TableCell>
              <TableCell>Text hash</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>Id</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dom.messages.map((m, i) => (
              <TableRow key={`${m.id}-${i}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{directionCell(m)}</TableCell>
                <TableCell>{m.timestamp}</TableCell>
                <TableCell>{m.textLength}</TableCell>
                <TableCell sx={MONO}>
                  {m.textHash}
                  {icpt && !icptByHash.has(m.textHash) ? (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}
                      · not in interceptor
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={MONO}>
                  {m.senderHash ?? '—'}
                  {m.hasPhone ? ' · phone' : ''}
                </TableCell>
                <TableCell sx={{ ...MONO, wordBreak: 'break-all' }}>{m.id}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Accordion disableGutters variant="outlined" sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">Page markup ({snap.rowsHtml.length} rows, text removed)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{ ...MONO, m: 0, maxHeight: 480, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
          >
            {snap.rowsHtml.join('\n\n')}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Section>
  );
};

export const SyncHistory: React.FC<{ report: DiagnosticsReport }> = ({ report }) => {
  const rows = [...(report.recentSyncAttempts ?? [])].reverse();
  return (
    <Section title="Recent syncs">
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No syncs recorded on this device.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Chat</TableCell>
              <TableCell>Messages sent</TableCell>
              <TableCell>Parser</TableCell>
              <TableCell>Server</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s, i) => {
              const ok = s.serverStatus >= 200 && s.serverStatus < 300;
              return (
                <TableRow key={`${s.ts}-${i}`}>
                  <TableCell>{fmt(s.ts)}</TableCell>
                  <TableCell>{s.action === 'force' ? 'Sync now' : 'Auto'}</TableCell>
                  <TableCell sx={MONO}>{s.phoneMasked}</TableCell>
                  <TableCell>{s.sent}</TableCell>
                  <TableCell>{s.parserUsed}</TableCell>
                  <TableCell>
                    <Chip size="small" color={ok ? 'success' : 'error'} variant="outlined" label={s.serverStatus} />
                    {s.serverMessage ? (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {s.serverMessage}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Section>
  );
};

export const Errors: React.FC<{ report: DiagnosticsReport }> = ({ report }) => {
  const errors = [...(report.errors ?? [])].reverse();
  if (errors.length === 0) return null;
  return (
    <Section title="Errors this session">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Severity</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Error</TableCell>
            <TableCell>Last API call</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {errors.map((e, i) => {
            const call = (e as { context?: { lastApiCall?: { endpoint: string; status: number; requestId?: string } } })
              .context?.lastApiCall;
            return (
              <TableRow key={i}>
                <TableCell>{fmt(e.timestamp)}</TableCell>
                <TableCell>{e.severity}</TableCell>
                <TableCell>{e.category}</TableCell>
                <TableCell sx={MONO}>{e.label}</TableCell>
                <TableCell sx={MONO}>
                  {call ? (
                    <>
                      {call.endpoint} → {call.status}
                      {call.requestId ? (
                        <>
                          {' · '}
                          <Link href={logsUrl(call.requestId)} target="_blank" rel="noopener noreferrer">
                            server logs
                          </Link>
                        </>
                      ) : null}
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Section>
  );
};

export const Trail: React.FC<{ report: DiagnosticsReport }> = ({ report }) => {
  const crumbs = [...(report.recentBreadcrumbs ?? [])].reverse();
  return (
    <Section title="What the user did (most recent first)">
      {crumbs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No activity recorded.
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {crumbs.map((c, i) => {
              const { requestId, ...rest } = (c.data ?? {}) as Record<string, unknown>;
              const details = Object.entries(rest)
                .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                .join(' · ');
              return (
                <TableRow key={`${c.ts}-${i}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap', width: 1 }}>{fmt(c.ts)}</TableCell>
                  <TableCell sx={{ width: 1 }}>
                    <Chip size="small" variant="outlined" label={c.action} />
                  </TableCell>
                  <TableCell sx={MONO}>
                    {details}
                    {typeof requestId === 'string' ? (
                      <>
                        {details ? ' · ' : ''}
                        <Link href={logsUrl(requestId)} target="_blank" rel="noopener noreferrer">
                          server logs
                        </Link>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Section>
  );
};
